/* Keep Focal's drawer, tabs, product rendering and focus trap; use Shopify search only. */
(() => {
  const init = () => {
    document.querySelectorAll('[data-ckl-native-search]').forEach((drawer) => {
      if (drawer.dataset.cklSearchReady) return;
      const input = drawer.querySelector('input[name="q"]');
      const results = drawer.querySelector('.predictive-search__results');
      const loading = drawer.querySelector('.predictive-search__loading-state');
      const form = drawer.querySelector('form');
      if (!input || !results || !loading || !form) return;
      drawer.dataset.cklSearchReady = 'shopify-v1';
      const cache = new Map();
      results.id = results.id || 'ckl-predictive-search-results';
      input.setAttribute('aria-controls', results.id);
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');

      const fallback = () => {
        const wrapper = document.createElement('div');
        const message = document.createElement('p');
        message.className = 'text--large';
        message.textContent = 'Suggestions are temporarily unavailable. You can still search all products.';
        const link = document.createElement('a');
        const url = new URL(form.action, location.origin);
        url.search = new URLSearchParams({ q: input.value.trim(), type: 'product', 'options[prefix]': 'last' });
        link.href = url.href;
        link.className = 'button button--primary';
        link.textContent = 'View search results';
        wrapper.append(message, link);
        return { hasResults: false, html: wrapper.innerHTML };
      };

      // The existing debounced _onSearch handler calls this method at request time.
      drawer._doPredictiveSearch = async function (term) {
        const settings = window.themeVariables?.settings || {};
        const types = String(settings.searchMode || 'product,collection,page')
          .split(',').filter((type) => ['product', 'collection', 'page', 'article'].includes(type));
        const url = new URL(window.themeVariables?.routes?.predictiveSearchUrl || '/search/suggest', location.origin);
        url.search = new URLSearchParams({
          q: term.trim(),
          'resources[type]': types.join(',') || 'product',
          'resources[limit]': '10',
          'resources[options][unavailable_products]': settings.searchUnavailableProducts || 'last',
          'resources[options][fields]': 'title,body,product_type,variants.title,variants.sku,vendor',
          section_id: 'predictive-search'
        });
        const signal = this.abortController?.signal;
        try {
          if (url.origin !== location.origin) throw new Error('Search must use the storefront origin');
          if (cache.has(url.href)) return cache.get(url.href);
          const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Predictive search HTTP ' + response.status);
          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = documentFragment.querySelector('#shopify-section-predictive-search');
          if (!section) throw new Error('Predictive search section missing');
          const result = { hasResults: !!section.querySelector('.predictive-search__results-categories'), html: section.innerHTML };
          if (cache.size >= 20) cache.delete(cache.keys().next().value);
          cache.set(url.href, result);
          return result;
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          return fallback();
        }
      };

      // Abort immediately, not just after the theme's 200ms debounce, to avoid stale results.
      input.addEventListener('input', () => {
        drawer.abortController?.abort();
        if (!input.value.trim()) {
          input.value = '';
          results.hidden = true;
          results.replaceChildren();
          loading.hidden = true;
          const footer = drawer.querySelector('.drawer__footer');
          if (footer) footer.hidden = true;
        }
      });
      const stateObserver = new MutationObserver(() => {
        results.setAttribute('aria-busy', String(!loading.hidden));
      });
      stateObserver.observe(loading, { attributes: true, attributeFilter: ['hidden'] });

      const visibleLinks = () => Array.from(results.querySelectorAll('a[href]'))
        .filter((link) => link.getClientRects().length && !link.closest('[hidden]'));
      drawer.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key) || results.hidden || event.altKey || event.ctrlKey || event.metaKey) return;
        const links = visibleLinks();
        if (!links.length) return;
        const index = links.indexOf(document.activeElement);
        if (document.activeElement !== input && index < 0) return;
        event.preventDefault();
        if (event.key === 'ArrowDown') links[(index + 1) % links.length].focus();
        else if (index <= 0) input.focus();
        else links[index - 1].focus();
      });
      // Avoid duplicate fields; collections have no full-search type, so fall back to products.
      form.addEventListener('formdata', (event) => {
        const active = results.querySelector('.predictive-search__results-categories-item:not([hidden])');
        const category = active?.id.replace('predictive-search-', '');
        const type = ({ products: 'product', articles: 'article', pages: 'page' })[category] || 'product';
        event.formData.set('type', type);
        event.formData.set('options[prefix]', 'last');
        event.formData.set('options[unavailable_products]', window.themeVariables?.settings?.searchUnavailableProducts || 'last');
        event.formData.set('q', input.value.trim());
      });
      form.addEventListener('submit', (event) => {
        if (!input.value.trim()) { event.preventDefault(); input.focus(); }
      });
    });
  };
  customElements.whenDefined('predictive-search-drawer').then(init);
  document.addEventListener('shopify:section:load', init);
})();
